import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import { glassPanel, glassTextField } from "./glass";
import { getAuthHeaders } from "./auth";
import defaultSiteLogo from "./imgs/Logo.png";

function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
  return Promise.resolve();
}

/**
 * Admin: hosted page branding + embed snippets for external sites.
 */
export default function WebsiteBrandingPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [copyTip, setCopyTip] = useState("");

  const [primaryColor, setPrimaryColor] = useState("#2d6a4f");
  const [accentColor, setAccentColor] = useState("#4caf50");
  const [showServiceCarousel, setShowServiceCarousel] = useState(true);
  const [footerAttribution, setFooterAttribution] = useState("");

  const [embedButtonLabel, setEmbedButtonLabel] = useState("Get a lawn quote");
  const [embedCorner, setEmbedCorner] = useState("bottom-right");
  const [publicQuoteAppUrl, setPublicQuoteAppUrl] = useState("");
  const [embedScriptOrigin, setEmbedScriptOrigin] = useState("");

  const [hasStoredLogo, setHasStoredLogo] = useState(false);
  const [logoPreviewKey, setLogoPreviewKey] = useState(0);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoFileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });
    try {
      const { data } = await axios.get("/site-settings/manage", { headers: getAuthHeaders() });
      const b = data.branding || {};
      setPrimaryColor(b.primaryColor || "#2d6a4f");
      setAccentColor(b.accentColor || "#4caf50");
      setShowServiceCarousel(b.showServiceCarousel !== false);
      setFooterAttribution(typeof b.footerAttribution === "string" ? b.footerAttribution : "");
      setHasStoredLogo(b.hasStoredLogo === true);
      const w = data.website || {};
      setEmbedButtonLabel(w.embedButtonLabel || "Get a lawn quote");
      setEmbedCorner(w.embedCorner === "bottom-left" ? "bottom-left" : "bottom-right");
      setPublicQuoteAppUrl(w.publicQuoteAppUrl || "");
      setEmbedScriptOrigin(w.embedScriptOrigin || "");
    } catch {
      setMessage({ type: "error", text: "Could not load website settings. Sign in as admin." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      await axios.patch(
        "/site-settings/website",
        {
          primaryColor: primaryColor.trim(),
          accentColor: accentColor.trim(),
          showServiceCarousel,
          footerAttribution,
          embedButtonLabel: embedButtonLabel.trim(),
          embedCorner,
          publicQuoteAppUrl: publicQuoteAppUrl.trim(),
          embedScriptOrigin: embedScriptOrigin.trim(),
        },
        { headers: getAuthHeaders() }
      );
      setMessage({
        type: "success",
        text: "Website settings saved. Open the quote page (or refresh) to see branding.",
      });
      await load();
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Save failed.",
      });
    } finally {
      setSaving(false);
    }
  }, [
    primaryColor,
    accentColor,
    showServiceCarousel,
    footerAttribution,
    embedButtonLabel,
    embedCorner,
    publicQuoteAppUrl,
    embedScriptOrigin,
    load,
  ]);

  const appUrlDisplay = publicQuoteAppUrl.trim() || `${window.location.origin}`;
  const scriptOriginDisplay =
    embedScriptOrigin.trim() ||
    (process.env.NODE_ENV === "development"
      ? `${window.location.protocol}//${window.location.hostname}:3001`
      : window.location.origin);

  const cornerShort = embedCorner === "bottom-left" ? "bl" : "br";

  const floatingButtonSnippet = useMemo(
    () => `<!-- Clibbins floating quote button: paste before </body> on your marketing site -->
<script>
  window.CLIBBINS_EMBED = {
    baseUrl: ${JSON.stringify(appUrlDisplay)},
    buttonLabel: ${JSON.stringify(embedButtonLabel)},
    corner: "${cornerShort}",
    primaryColor: ${JSON.stringify(primaryColor.trim())}
  };
</script>
<script src="${scriptOriginDisplay.replace(/\/$/, "")}/embed/quote-button.js" async></script>`,
    [appUrlDisplay, embedButtonLabel, cornerShort, primaryColor, scriptOriginDisplay]
  );

  const iframeSnippet = useMemo(
    () => `<!-- Clibbins quote experience in an iframe -->
<iframe
  src="${appUrlDisplay.replace(/\/$/, "")}/quote?embed=1"
  title="Request a lawn care quote"
  style="width:100%;min-height:720px;border:0;border-radius:16px;box-shadow:0 8px 32px rgba(0,0,0,0.08);"
  loading="lazy"
></iframe>`,
    [appUrlDisplay]
  );

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLogoUploading(true);
    setMessage({ type: "", text: "" });
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const { data } = await axios.post("/site-settings/branding-logo", fd, {
        headers: getAuthHeaders(),
      });
      if (data.branding) {
        setHasStoredLogo(data.branding.hasStoredLogo === true);
        setLogoPreviewKey((k) => k + 1);
      }
      setMessage({
        type: "success",
        text: "Logo updated. Visitors will see it in the top-left corner after they refresh.",
      });
      await load();
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Logo upload failed.",
      });
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = async () => {
    setLogoUploading(true);
    setMessage({ type: "", text: "" });
    try {
      await axios.delete("/site-settings/branding-logo", { headers: getAuthHeaders() });
      setHasStoredLogo(false);
      setLogoPreviewKey((k) => k + 1);
      setMessage({
        type: "success",
        text: "Custom logo removed. The default app logo is shown again.",
      });
      await load();
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not remove logo.",
      });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleCopy = (text, label) => {
    copyToClipboard(text).then(() => {
      setCopyTip(label);
      setTimeout(() => setCopyTip(""), 2500);
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">
          Loading website options…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {message.text ? (
        <Alert severity={message.type === "success" ? "success" : "error"} sx={{ borderRadius: 2 }}>
          {message.text}
        </Alert>
      ) : null}

      <Box sx={{ ...glassPanel, borderRadius: 3, p: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Hosted quote page (this app)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
          Tune the public quote experience customers see at <strong>/quote</strong> when they use your Clibbins URL.
          Hero headline and image stay in the section above.
        </Typography>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Site logo
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Shown in the top-left on the quote flow, dashboard, and login for everyone (PNG, JPEG, WebP, or GIF, max
          512 KB).
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            component="img"
            src={hasStoredLogo ? `/site-settings/logo?t=${logoPreviewKey}` : defaultSiteLogo}
            alt=""
            sx={{
              width: 64,
              height: 64,
              objectFit: "contain",
              borderRadius: 2,
              border: "1px solid rgba(0,0,0,0.08)",
              bgcolor: "rgba(255,255,255,0.5)",
            }}
          />
          <input
            ref={logoFileRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
            hidden
            onChange={handleLogoFileChange}
          />
          <Button
            variant="outlined"
            size="small"
            disabled={logoUploading}
            onClick={() => logoFileRef.current?.click()}
          >
            Upload logo
          </Button>
          <Button
            variant="text"
            size="small"
            color="inherit"
            disabled={logoUploading || !hasStoredLogo}
            onClick={removeLogo}
          >
            Remove custom logo
          </Button>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <TextField
            label="Primary color"
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            sx={{ width: 140, ...glassTextField }}
            InputProps={{ sx: { fontFamily: "monospace" } }}
            helperText="#RRGGBB"
          />
          <TextField
            label="Accent color"
            value={accentColor}
            onChange={(e) => setAccentColor(e.target.value)}
            sx={{ width: 140, ...glassTextField }}
            InputProps={{ sx: { fontFamily: "monospace" } }}
            helperText="#RRGGBB"
          />
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 200 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showServiceCarousel}
                  onChange={(e) => setShowServiceCarousel(e.target.checked)}
                  color="primary"
                />
              }
              label="Show service carousel"
            />
          </Box>
        </Box>
        <TextField
          label="Footer line (optional)"
          fullWidth
          multiline
          minRows={2}
          value={footerAttribution}
          onChange={(e) => setFooterAttribution(e.target.value)}
          sx={{ mb: 2, ...glassTextField }}
          helperText="Short trust line, license text, or business name under the quote flow."
        />
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.35)" }} />

      <Box sx={{ ...glassPanel, borderRadius: 3, p: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Use on your existing website
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
          Keep your current marketing site and add a floating button or iframe. Set the URLs below so the
          snippets point to the right places in production.
        </Typography>
        <TextField
          label="Public quote app URL"
          fullWidth
          value={publicQuoteAppUrl}
          onChange={(e) => setPublicQuoteAppUrl(e.target.value)}
          placeholder={typeof window !== "undefined" ? window.location.origin : "https://quotes.example.com"}
          sx={{ mb: 2, ...glassTextField }}
          helperText="Where this React app is hosted (used in embed links). Leave blank to use the current browser origin in the snippet."
        />
        <TextField
          label="API origin (embed script)"
          fullWidth
          value={embedScriptOrigin}
          onChange={(e) => setEmbedScriptOrigin(e.target.value)}
          placeholder="https://api.example.com or http://localhost:3001"
          sx={{ mb: 2, ...glassTextField }}
          helperText="Server that runs this Clibbins API (serves /embed/quote-button.js). Dev default: port 3001 on same host."
        />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <TextField
            label="Button label"
            value={embedButtonLabel}
            onChange={(e) => setEmbedButtonLabel(e.target.value)}
            sx={{ flex: "1 1 200px", minWidth: 180, ...glassTextField }}
          />
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="corner-label">Button corner</InputLabel>
            <Select
              labelId="corner-label"
              label="Button corner"
              value={embedCorner}
              onChange={(e) => setEmbedCorner(e.target.value)}
            >
              <MenuItem value="bottom-right">Bottom right</MenuItem>
              <MenuItem value="bottom-left">Bottom left</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1, mb: 1 }}>
          Floating button (script)
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Loads a pill button that opens the quote flow in a new tab (<code>?embed=1</code> hides app chrome).
        </Typography>
        <TextField
          multiline
          fullWidth
          value={floatingButtonSnippet}
          InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 12 } }}
          minRows={8}
          sx={{ mb: 1, ...glassTextField }}
        />
        <Button
          size="small"
          startIcon={<ContentCopyIcon />}
          onClick={() => handleCopy(floatingButtonSnippet, "button")}
        >
          Copy floating button code
        </Button>

        <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
          Inline iframe
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Embeds the full quote page in your layout (still uses <code>?embed=1</code>).
        </Typography>
        <TextField
          multiline
          fullWidth
          value={iframeSnippet}
          InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 12 } }}
          minRows={6}
          sx={{ mb: 1, ...glassTextField }}
        />
        <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => handleCopy(iframeSnippet, "iframe")}>
          Copy iframe code
        </Button>
        {copyTip ? (
          <Typography variant="caption" color="success.main" sx={{ display: "block", mt: 1 }}>
            Copied {copyTip === "button" ? "button snippet" : "iframe snippet"}.
          </Typography>
        ) : null}
      </Box>

      <Button variant="contained" color="primary" onClick={save} disabled={saving}>
        {saving ? "Saving…" : "Save website & embed settings"}
      </Button>
    </Box>
  );
}
